import { useCallback, type MouseEvent } from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@lineage/ui'
import { Checkbox } from '@/components/ui/checkbox'
import { Slider } from '@/components/ui/slider'
import { Textarea } from '@/components/ui/textarea'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ChevronDown,
  ChevronDownIcon,
  ChevronRight,
  Maximize2,
  Trash2,
  Ungroup,
  Lock,
  Unlock,
  Copy,
  MousePointer2,
  Download,
  LayoutGrid,
  ArrowRight,
  ArrowDown,
  Circle,
  Tag,
  Palette,
  Settings2,
  Zap,
  Info,
} from 'lucide-react'
import { NODE_COLOR_OPTIONS, type NodeColor } from '@/lib/node-colors'
import { cn } from '@/lib/utils'

/** Auto-layout modes for arranging children */
export type AutoLayoutMode = 'none' | 'grid' | 'horizontal' | 'vertical'

/** Border style options */
export type BorderStyle = 'solid' | 'dashed' | 'dotted'

interface SubFlowInspectorProps {
  data: {
    label?: string
    collapsed?: boolean
    childCount?: number
    /** Color theme for the group */
    color?: NodeColor
    /** Description/notes for the group */
    description?: string
    /** Whether the group is locked (prevents movement/editing) */
    locked?: boolean
    /** Background opacity (0-100) */
    opacity?: number
    /** Inner padding in pixels */
    padding?: number
    /** Auto-layout mode for children */
    autoLayout?: AutoLayoutMode
    /** Border style */
    borderStyle?: BorderStyle
  }
  onChange: (updates: Partial<SubFlowInspectorProps['data']>) => void
  onAutoResize?: () => void
  onUngroup?: () => void
  onDelete?: () => void
  onToggleCollapse?: () => void
  /** Duplicate the group with all children */
  onDuplicate?: () => void
  /** Select all children nodes */
  onSelectAllChildren?: () => void
  /** Export group as image */
  onExportAsImage?: () => void
  /** Apply auto-layout to children */
  onApplyAutoLayout?: (mode: AutoLayoutMode) => void
  /** Called when drag starts (for FloatingPanel integration) */
  onDragStart?: (e: MouseEvent) => void
}

/** Color swatch button for the color picker */
function ColorSwatch({
  color,
  selected,
  onClick,
  disabled,
}: {
  color: NodeColor
  selected: boolean
  onClick: () => void
  disabled?: boolean
}) {
  const colorClasses: Record<NodeColor, string> = {
    neutral: 'bg-neutral-500',
    sky: 'bg-sky-500',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    violet: 'bg-violet-500',
  }

  const colorNames: Record<NodeColor, string> = {
    neutral: 'Gris',
    sky: 'Azul',
    emerald: 'Verde',
    amber: 'Ámbar',
    violet: 'Violeta',
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
              'w-6 h-6 rounded-full transition-all',
              colorClasses[color],
              selected
                ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110'
                : 'hover:scale-110',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            aria-label={`Color ${colorNames[color]}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {colorNames[color]}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default function SubFlowInspector({
  data,
  onChange,
  onAutoResize,
  onUngroup,
  onDelete,
  onToggleCollapse,
  onDuplicate,
  onSelectAllChildren,
  onExportAsImage,
  onApplyAutoLayout,
  onDragStart,
}: SubFlowInspectorProps) {
  const isLocked = data.locked || false

  // Handle auto-layout change
  const handleAutoLayoutChange = useCallback(
    (mode: AutoLayoutMode) => {
      onChange({ autoLayout: mode })
      onApplyAutoLayout?.(mode)
    },
    [onChange, onApplyAutoLayout]
  )

  return (
    <div className="pointer-events-auto">
      {/* Header with lock indicator - draggable when onDragStart is provided */}
      <div
        className={cn(
          "p-3 border-b flex items-center justify-between",
          onDragStart && "cursor-grab active:cursor-grabbing select-none"
        )}
        onMouseDown={onDragStart}
      >
        <div>
          <h3 className="text-sm font-semibold">Propiedades del Grupo</h3>
          <p className="text-[11px] text-muted-foreground">
            Configura nombre, apariencia y comportamiento
          </p>
        </div>
        {isLocked && (
          <span className="text-[10px] text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded flex items-center gap-1">
            <Lock className="h-3 w-3" />
            Bloqueado
          </span>
        )}
      </div>

      <Accordion
        type="multiple"
        className="w-full"
        defaultValue={['identification', 'appearance', 'state', 'actions']}
      >
        {/* Section 1: Identificación */}
        <AccordionItem value="identification" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Tag size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Identificación</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Nombre y descripción del grupo
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Name */}
              <div className="space-y-1.5">
                <Label htmlFor="group-label" className="text-xs">
                  Nombre del Grupo
                </Label>
                <Input
                  id="group-label"
                  type="text"
                  value={data.label || ''}
                  onChange={(e) => onChange({ label: e.target.value })}
                  placeholder="Grupo Sin Nombre"
                  disabled={isLocked}
                  className="h-8 text-sm"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="group-description" className="text-xs">
                  Descripción / Notas
                </Label>
                <Textarea
                  id="group-description"
                  value={data.description || ''}
                  onChange={(e) => onChange({ description: e.target.value })}
                  placeholder="Añade notas sobre este grupo..."
                  className="min-h-[60px] text-xs resize-none"
                  disabled={isLocked}
                />
              </div>

              {/* Child Count (Read-only) */}
              {data.childCount !== undefined && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Nodos en el Grupo</Label>
                  <div className="text-xs text-muted-foreground bg-muted px-2 py-1.5 rounded-md flex items-center justify-between">
                    <span>
                      {data.childCount} {data.childCount === 1 ? 'nodo' : 'nodos'}
                    </span>
                    {onSelectAllChildren && data.childCount > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={onSelectAllChildren}
                        className="h-6 px-2 text-[10px]"
                        disabled={isLocked}
                      >
                        <MousePointer2 className="h-3 w-3 mr-1" />
                        Seleccionar
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 2: Apariencia */}
        <AccordionItem value="appearance" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Palette size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Apariencia</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Color, opacidad y estilo visual
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-4">
              {/* Color Picker */}
              <div className="space-y-1.5">
                <Label className="text-xs">Color del Grupo</Label>
                <div className="flex items-center gap-2">
                  {NODE_COLOR_OPTIONS.map((color) => (
                    <ColorSwatch
                      key={color}
                      color={color}
                      selected={data.color === color || (!data.color && color === 'neutral')}
                      onClick={() => onChange({ color })}
                      disabled={isLocked}
                    />
                  ))}
                </div>
              </div>

              {/* Opacity */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Opacidad del fondo</Label>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {data.opacity ?? 10}%
                  </span>
                </div>
                <Slider
                  value={[data.opacity ?? 10]}
                  onValueChange={([value]) => onChange({ opacity: value })}
                  min={0}
                  max={100}
                  step={5}
                  disabled={isLocked}
                  className="w-full"
                />
              </div>

              {/* Padding */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Padding interior</Label>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {data.padding ?? 20}px
                  </span>
                </div>
                <Slider
                  value={[data.padding ?? 20]}
                  onValueChange={([value]) => onChange({ padding: value })}
                  min={8}
                  max={64}
                  step={4}
                  disabled={isLocked}
                  className="w-full"
                />
              </div>

              {/* Border Style */}
              <div className="space-y-1.5">
                <Label className="text-xs">Estilo del borde</Label>
                <Select
                  value={data.borderStyle || 'solid'}
                  onValueChange={(value: BorderStyle) => onChange({ borderStyle: value })}
                  disabled={isLocked}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Seleccionar estilo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solid">
                      <span className="flex items-center gap-2">
                        <span className="w-8 border-t-2 border-current" />
                        Sólido
                      </span>
                    </SelectItem>
                    <SelectItem value="dashed">
                      <span className="flex items-center gap-2">
                        <span className="w-8 border-t-2 border-dashed border-current" />
                        Discontinuo
                      </span>
                    </SelectItem>
                    <SelectItem value="dotted">
                      <span className="flex items-center gap-2">
                        <span className="w-8 border-t-2 border-dotted border-current" />
                        Punteado
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 3: Estado */}
        <AccordionItem value="state" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Settings2 size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Estado</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Bloqueo y colapso del grupo
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-3">
              {/* Lock */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="locked"
                  checked={isLocked}
                  onCheckedChange={(checked) => onChange({ locked: checked === true })}
                />
                <Label htmlFor="locked" className="cursor-pointer text-xs flex items-center gap-1.5">
                  {isLocked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                  Bloquear Grupo
                </Label>
              </div>
              <p className="text-[10px] text-muted-foreground ml-6">
                Impide mover y editar el grupo
              </p>

              {/* Collapsed */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="collapsed"
                  checked={data.collapsed || false}
                  onCheckedChange={(checked) => {
                    if (onToggleCollapse) {
                      onToggleCollapse()
                    } else {
                      onChange({ collapsed: checked === true })
                    }
                  }}
                  disabled={isLocked}
                />
                <Label htmlFor="collapsed" className="cursor-pointer text-xs">
                  Grupo Colapsado
                </Label>
              </div>
              <p className="text-[10px] text-muted-foreground ml-6">
                Oculta los nodos hijos temporalmente
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Section 4: Auto-Layout (solo si no está colapsado) */}
        {!data.collapsed && (
          <AccordionItem value="layout" className="py-1">
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
                <span className="flex items-center gap-2">
                  <span
                    className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                    aria-hidden="true"
                  >
                    <LayoutGrid size={14} className="opacity-60" />
                  </span>
                  <span className="flex flex-col">
                    <span>Auto-Layout</span>
                    <span className="text-[11px] font-normal text-muted-foreground">
                      Organiza los nodos automáticamente
                    </span>
                  </span>
                </span>
                <ChevronDownIcon
                  size={14}
                  className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                  aria-hidden="true"
                />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionContent className="px-3 pb-3">
              <div className="ml-10 space-y-2">
                <div className="grid grid-cols-4 gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={data.autoLayout === 'none' || !data.autoLayout ? 'secondary' : 'outline'}
                          onClick={() => handleAutoLayoutChange('none')}
                          className="h-8 px-2"
                          disabled={isLocked}
                        >
                          <Circle className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Libre</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={data.autoLayout === 'horizontal' ? 'secondary' : 'outline'}
                          onClick={() => handleAutoLayoutChange('horizontal')}
                          className="h-8 px-2"
                          disabled={isLocked}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Horizontal</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={data.autoLayout === 'vertical' ? 'secondary' : 'outline'}
                          onClick={() => handleAutoLayoutChange('vertical')}
                          className="h-8 px-2"
                          disabled={isLocked}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Vertical</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant={data.autoLayout === 'grid' ? 'secondary' : 'outline'}
                          onClick={() => handleAutoLayoutChange('grid')}
                          className="h-8 px-2"
                          disabled={isLocked}
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Grid</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Distribuye los nodos hijos automáticamente
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Section 5: Acciones */}
        <AccordionItem value="actions" className="py-1">
          <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between px-3 py-2 text-left text-[13px] leading-6 font-semibold transition-all outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
              <span className="flex items-center gap-2">
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-background"
                  aria-hidden="true"
                >
                  <Zap size={14} className="opacity-60" />
                </span>
                <span className="flex flex-col">
                  <span>Acciones</span>
                  <span className="text-[11px] font-normal text-muted-foreground">
                    Operaciones rápidas sobre el grupo
                  </span>
                </span>
              </span>
              <ChevronDownIcon
                size={14}
                className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
                aria-hidden="true"
              />
            </AccordionPrimitive.Trigger>
          </AccordionPrimitive.Header>
          <AccordionContent className="px-3 pb-3">
            <div className="ml-10 space-y-1.5">
              {/* Toggle Collapse */}
              {onToggleCollapse && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onToggleCollapse}
                  className="w-full justify-start h-8 text-xs"
                  disabled={isLocked}
                >
                  {data.collapsed ? (
                    <>
                      <ChevronRight className="h-3.5 w-3.5 mr-2" />
                      Expandir Grupo
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3.5 w-3.5 mr-2" />
                      Colapsar Grupo
                    </>
                  )}
                </Button>
              )}

              {/* Auto-resize */}
              {!data.collapsed && onAutoResize && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onAutoResize}
                  className="w-full justify-start h-8 text-xs"
                  disabled={isLocked}
                >
                  <Maximize2 className="h-3.5 w-3.5 mr-2" />
                  Auto-ajustar Tamaño
                </Button>
              )}

              {/* Duplicate */}
              {onDuplicate && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onDuplicate}
                  className="w-full justify-start h-8 text-xs"
                >
                  <Copy className="h-3.5 w-3.5 mr-2" />
                  Duplicar Grupo
                </Button>
              )}

              {/* Export as Image */}
              {onExportAsImage && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onExportAsImage}
                  className="w-full justify-start h-8 text-xs"
                >
                  <Download className="h-3.5 w-3.5 mr-2" />
                  Exportar como Imagen
                </Button>
              )}

              {/* Ungroup */}
              {onUngroup && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onUngroup}
                  className="w-full justify-start h-8 text-xs"
                  disabled={isLocked}
                >
                  <Ungroup className="h-3.5 w-3.5 mr-2" />
                  Desagrupar
                </Button>
              )}

              {/* Delete */}
              {onDelete && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={onDelete}
                  className="w-full justify-start h-8 text-xs"
                  disabled={isLocked}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Eliminar Grupo
                </Button>
              )}

              {/* Tip */}
              <div className="flex items-start gap-1.5 pt-2 text-[10px] text-muted-foreground">
                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  <strong>Tip:</strong> Arrastra nodos para añadirlos o liberarlos del grupo
                </span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
